import { Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { CurrencyCode } from "@smartrr/shared/currencyCode";
import { ISmartrrSellingPlan, SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import React from "react";

import { formatGetPrice } from "@customer-app/account/views/OperationAquarius/utils/Pricing";
import {
  getItemPriceWithSellingPlan,
  getOrderCount,
} from "@customer-app/account/views/OperationAquarius/utils/Pricing/Item";
import { VariantPurchaseOptions } from "@customer-app/state/reducers/sellingPlanGroups";

import { AddonPricingProps } from "../types";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

export const AddonPricing = ({
  currency,
  variantFromDropdown,
  enabledVariantOptions,
  configType,
  delivery,
}: AddonPricingProps): JSX.Element => {
  const customerCountryCode = useSmartrrCustomerSelector(
    state => state.auth.customerRelationship?.defaultAddress?.countryCode
  );

  const variant = variantFromDropdown
    ? enabledVariantOptions.find(item => item.vnt.id === variantFromDropdown.id) || enabledVariantOptions[0]
    : enabledVariantOptions.find(item => !!item.addonConfig) || enabledVariantOptions[0];

  const getDiscountPlan = (variant: VariantPurchaseOptions): ISmartrrSellingPlan | null | undefined => {
    if (!variant) {
      return null;
    }

    switch (configType) {
      case SellingPlanConfigs.ADDON: {
        if (variant.addonConfig) {
          return variant.addonConfig;
        }
        break;
      }
      case SellingPlanConfigs.TRENDING: {
        if (variant.trendingConfig) {
          return variant.trendingConfig;
        }
        break;
      }
    }
    return variant.sellingPlan;
  };

  if (!variant) {
    return <React.Fragment />;
  }

  const discountPlan = getDiscountPlan(variant);

  const newPrice = formatGetPrice(
    getItemPriceWithSellingPlan(
      variant.vnt,
      delivery.purchaseState.currency,
      discountPlan,
      getOrderCount(delivery),
      undefined,
      customerCountryCode
    ),
    currency as CurrencyCode
  );

  const hasDiscount = newPrice.current !== newPrice.initial;

  return (
    <Stack
      direction="row"
      sx={{ marginBottom: "3px", flexWrap: "wrap" }}
      className={prefixClassName("addon-pricing-container")}
    >
      <Typography
        variant="caption1"
        className={prefixClassName("product-after-discount-price")}
        data-testid="after-discount-price"
        sx={{ marginRight: "3px" }}
      >
        {newPrice.current}
      </Typography>

      {!!hasDiscount && (
        <Typography
          variant="caption1"
          style={{ textDecoration: "line-through", opacity: "70%" }}
          className={prefixClassName("product-before-discount-price")}
          data-testid="before-discount-price"
        >
          {newPrice.initial}
        </Typography>
      )}
    </Stack>
  );
};
